import React, { useEffect, useMemo, Fragment } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { useParams } from 'react-router';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { getTimestampAsReadableString } from 'libs/end-to-end/src/dates';
import { UserUploadModal } from 'apps/sigma2-ui/src/users/components/UserUploadModal';
import { useDataSetPullUploadPage } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetPullUploadPage';
import { ButtonWithConfirmation } from 'apps/sigma2-ui/src/components/ButtonWithConfirmation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { DataSetVersionFileSelector } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetVersionFileSelector';
import { Transition } from '@headlessui/react';
import { CircularProgress } from '@mui/material';

export const DataSetPullUploadPage = () => {
  const { dataSetId, databaseId, organizationId } = useParams();
  const language = useLanguage();

  if (!dataSetId || !databaseId || !organizationId) {
    throw new Error('Missing required parameters');
  }

  const {
    uploadDataSetTitle,
    displayMode,
    pullButtonBehavior,
    pullButtonLabel,
    pullButtonDisabled,
    backButtonBehavior,
    backButtonLabel,
    backButtonDisabled,
    uploadFileButtonBehavior,
    uploadFileButtonLabel,
    uploadFileButtonDisabled,
    isModalOpen,
    setIsModalOpen,
    tryCreateVersion,
    userId,
    versions,
    runPostProcessingForVersion,
    currentPage,
    setCurrentPage,
    totalPages,
    canGoToPreviousPage,
    canGoToNextPage,
    showPullToast,
    setShowPullToast,
    showSuccessToast,
    setShowSuccessToast,
    showErrorToast,
    setShowErrorToast,
    isLoadingVersions,
  } = useDataSetPullUploadPage({
    dataSetId,
    databaseId,
    organizationId,
  });

  useEffect(() => {
    if (showSuccessToast) {
      const timer = setTimeout(() => {
        setShowSuccessToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessToast, setShowSuccessToast]);

  useEffect(() => {
    if (showErrorToast) {
      const timer = setTimeout(() => {
        setShowErrorToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showErrorToast, setShowErrorToast]);

  return (
    <AdminLayout currentPage={'DataSetPullUpload'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Databases',
            to: `/organizations/${organizationId}/databases`,
          },
          {
            label: 'Edit Database',
            to: `/organizations/${organizationId}/databases/edit/${databaseId}`,
          },
          {
            label: 'Data Sets',
            to: `/organizations/${organizationId}/databases/${databaseId}/data-sets`,
          },
          {
            label: 'Pull or Upload',
            to: `/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/pull-upload`,
          },
        ]}
      />
      <Spacing size={'md'} direction={'vertical'}>
        <Paragraph>{uploadDataSetTitle}</Paragraph>

        {versions.pagination &&
          versions.pagination.total > 0 &&
          totalPages > 1 && (
            <Paragraph>
              <strong>
                Showing page {currentPage} of {totalPages} (
                {versions.pagination.total} total versions)
              </strong>
            </Paragraph>
          )}

        <div
          style={{
            position: 'relative',
            minHeight: isLoadingVersions ? '200px' : undefined,
          }}
        >
          {isLoadingVersions && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </div>
          )}
          <Table
            items={versions.versions}
            columnDefinitions={[
              {
                id: 'dataSetVersion',
                header: () => <div>Data Set Version</div>,
                cell: ({ item }) => (
                  <div>{item.dataSetVersion ? item.dataSetVersion : 'N/A'}</div>
                ),
              },
              {
                id: 'status',
                header: () => <div>Status</div>,
                cell: ({ item }) => <div>{item.status}</div>,
              },
              {
                id: 'createdAt',
                header: () => <div>Created At</div>,
                cell: ({ item }) => (
                  <div>
                    {item.createdAt
                      ? getTimestampAsReadableString(item.createdAt)
                      : 'N/A'}
                  </div>
                ),
              },
              {
                id: 'updatedAt',
                header: () => <div>Updated At</div>,
                cell: ({ item }) => (
                  <div>
                    {item.updatedAt
                      ? getTimestampAsReadableString(item.updatedAt)
                      : 'N/A'}
                  </div>
                ),
              },
              {
                id: 'files',
                header: () => <div>Files</div>,
                cell: ({ item }) => {
                  return (
                    <DataSetVersionFileSelector
                      organizationId={organizationId}
                      databaseId={databaseId}
                      dataSetId={dataSetId}
                      dataSetVersion={item.dataSetVersion}
                      automationTag={null}
                      initialFileInfo={{
                        fileConsoleUrls:
                          versions.versionFiles[item.dataSetVersion]
                            .fileConsoleUrls,
                        totalNumberOfFiles:
                          versions.totalNumberOfVersionFiles[
                            item.dataSetVersion
                          ],
                      }}
                    />
                  );
                },
              },
              {
                id: 'postProcessing',
                header: () => <div>Post Processing</div>,
                cell: ({ item }) => {
                  if (
                    item.status === 'NotStarted' ||
                    item.status === 'Processing'
                  ) {
                    return 'N/A';
                  }

                  return (
                    <Spacing size={'md'} direction={'vertical'}>
                      <ButtonWithConfirmation
                        buttonTranslatedContent={language.runPostProcessingForVersionButton()}
                        confirmationTranslatedContent={language.runPostProcessingForVersionConfirmation()}
                        onClick={() => {
                          runPostProcessingForVersion(
                            item.dataSetVersion,
                            false,
                          );
                        }}
                      />
                      <ButtonWithConfirmation
                        buttonTranslatedContent={language.runPostProcessingAndWorkflowsForVersionButton()}
                        confirmationTranslatedContent={language.runPostProcessingForVersionConfirmation()}
                        onClick={() => {
                          runPostProcessingForVersion(
                            item.dataSetVersion,
                            true,
                          );
                        }}
                      />
                    </Spacing>
                  );
                },
              },
            ]}
          ></Table>
        </div>

        <div style={{ paddingBottom: '24px' }}>
          <Spacing size="sm" direction="horizontal">
            <Button
              behavior={backButtonBehavior}
              text={backButtonLabel}
              disabled={backButtonDisabled}
            />

            {displayMode === 'upload' && userId && (
              <>
                <Button
                  behavior={uploadFileButtonBehavior}
                  text={uploadFileButtonLabel}
                  disabled={uploadFileButtonDisabled}
                />
                {isModalOpen && (
                  <UserUploadModal
                    onClose={() => setIsModalOpen(false)}
                    onComplete={(userUploadId) => {
                      tryCreateVersion({ userUploadId });
                    }}
                    userId={userId}
                  />
                )}
              </>
            )}

            {displayMode === 'pull' && (
              <Button
                behavior={pullButtonBehavior}
                text={pullButtonLabel}
                disabled={pullButtonDisabled}
              />
            )}

            {/* Pagination controls */}
            {versions.pagination && totalPages > 1 && (
              <>
                <div style={{ flex: 1 }} />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: () => setCurrentPage(currentPage - 1),
                      }}
                      text="← Prev"
                      disabled={!canGoToPreviousPage}
                    />
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: () => setCurrentPage(currentPage + 1),
                      }}
                      text="Next →"
                      disabled={!canGoToNextPage}
                    />
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    Page {currentPage} of {totalPages}
                  </div>
                </div>
              </>
            )}
          </Spacing>
        </div>
      </Spacing>

      <div className="fixed end-4 bottom-4 z-50 flex flex-col gap-2">
        <Transition appear={true} show={showPullToast} as={Fragment}>
          <Transition.Child
            as={'div'}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="w-[384px] bg-blue-600 text-white rounded-lg shadow-2xl p-4">
              <div className="flex items-center gap-3">
                <CircularProgress size={20} sx={{ color: 'white' }} />
                <span className="font-medium">Pulling new dataset...</span>
              </div>
            </div>
          </Transition.Child>
        </Transition>

        {/* Success toast */}
        <Transition appear={true} show={showSuccessToast} as={Fragment}>
          <Transition.Child
            as={'div'}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="w-[384px] bg-green-600 text-white rounded-lg shadow-2xl p-4">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">
                  Dataset pull completed successfully!
                </span>
              </div>
            </div>
          </Transition.Child>
        </Transition>

        {/* Error toast */}
        <Transition appear={true} show={showErrorToast} as={Fragment}>
          <Transition.Child
            as={'div'}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="w-[384px] bg-red-600 text-white rounded-lg shadow-2xl p-4">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">Dataset pull failed</span>
              </div>
            </div>
          </Transition.Child>
        </Transition>
      </div>
    </AdminLayout>
  );
};
